import type {
  AgentAuditList,
  AgentDetail,
  AgentList,
  AgentSummary,
  AgentUpdate,
  LineGreeting,
  LineGreetingUpdate,
} from '@inbound/contract';

import { apiSlice } from './api-slice.js';

/**
 * Agent configuration (S10).
 *
 * No polling. These records change when a person changes them — here or in the
 * platform's own dashboard — and polling an editor would mean pulling text out
 * from under whoever is typing. The stale case is handled where it actually
 * matters instead: a save is refused if the agent moved since it was loaded, and
 * the message says who moved it.
 */
export const agentsApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getAgents: build.query<AgentList, void>({
      query: () => ({ url: '/agents' }),
      providesTags: ['Agent'],
    }),

    getAgent: build.query<AgentDetail, string>({
      query: (id) => ({ url: `/agents/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'Agent' as const, id }],
    }),

    /**
     * Save an edit.
     *
     * Invalidates the list as well as the record, since the list shows the name,
     * and the greetings: a rename rewrites every greeting that uses `{agent}`.
     */
    updateAgent: build.mutation<AgentDetail, { id: string; body: AgentUpdate }>({
      query: ({ id, body }) => ({ url: `/agents/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Agent' as const, id },
        'Agent',
        'Greeting',
      ],
    }),

    /** What a caller on this line hears first. */
    getLineGreeting: build.query<LineGreeting, string>({
      query: (lineId) => ({ url: `/lines/${lineId}/greeting` }),
      providesTags: (_result, _error, lineId) => [{ type: 'Greeting' as const, id: lineId }],
    }),

    /** Also refreshes the agent's audit trail, where greeting changes are recorded. */
    updateLineGreeting: build.mutation<LineGreeting, { lineId: string; body: LineGreetingUpdate }>({
      query: ({ lineId, body }) => ({ url: `/lines/${lineId}/greeting`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { lineId }) => [
        { type: 'Greeting' as const, id: lineId },
        'Agent',
      ],
    }),

    getAgentAudit: build.query<AgentAuditList, string>({
      query: (id) => ({ url: `/agents/${id}/audit` }),
      providesTags: (_result, _error, id) => [{ type: 'Agent' as const, id }],
    }),
  }),
});

export const {
  useGetAgentsQuery,
  useGetAgentQuery,
  useUpdateAgentMutation,
  useGetAgentAuditQuery,
  useGetLineGreetingQuery,
  useUpdateLineGreetingMutation,
} = agentsApi;

/** What the list calls each agent, in the operator's terms rather than the enum's. */
export function agentRoleLabel(agent: Pick<AgentSummary, 'role'>): string {
  return agent.role === 'CONVERSATION' ? 'Answers the calls' : 'Analyses the calls';
}
